import { useEffect, useRef } from 'react';

export interface UseInfiniteScrollOptions {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
}

/**
 * Hook for implementing infinite scroll with Intersection Observer
 * @param onLoadMore - Callback to load more items
 * @param hasMore - Whether more items are available
 * @param isLoading - Whether items are currently being loaded
 * @param threshold - Distance in pixels from bottom to trigger load (default: 200)
 * @returns Ref to attach to sentinel element
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 200,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => { onLoadMoreRef.current = onLoadMore; }, [onLoadMore]);
  
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        rootMargin: `${threshold}px`,
      }
    );
    
    observer.observe(sentinelRef.current);
    
    return () => observer.disconnect();
  }, [hasMore, isLoading, threshold]);
  
  return sentinelRef;
}
